import { AppError } from "../../../shared/utils/error-handler";
import type { CompanyProductRepository } from "../../repositories/company/company-product-repository";
import type {
  CreateProductRequest, DetailPageDto,
  LinkProductOptionsRequest,
  ProductDto,
  ProductManagementDto
} from "../../types/company/company-product";
import type { UpdateProductRequest } from "../../../shared/types/api";

export class CompanyProductUsecase {
  constructor(private companyProductRepository: CompanyProductRepository) {}

  async getProducts(companyId: number): Promise<ProductManagementDto | ProductDto[]> {
    try {
      return await this.companyProductRepository.getProducts(companyId);
    } catch (error) {
      throw new AppError(
        String(error) || "상품들을 불러오지 못했습니다.",
        "PRODUCTS_GET_FAILED",
        error,
      );
    }
  }

  async getProduct(companyId: number, productId: number): Promise<ProductDto> {
    try {
      return await this.companyProductRepository.getProduct(
        companyId,
        productId,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "상품을 불러오지 못했습니다.",
        "PRODUCT_GET_FAILED",
        error,
      );
    }
  }

  async createProduct(
    companyId: number,
    request: CreateProductRequest,
    thumbnailFile?: File | null,
    detailPageFiles?: File[] | null,
  ): Promise<ProductDto> {
    try {
      return await this.companyProductRepository.createProduct(
        companyId,
        request,
        thumbnailFile,
        detailPageFiles,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "상품을 생성하지 못했습니다.",
        "PRODUCT_CREATE_FAILED",
        error,
      );
    }
  }

  async updateProduct(
    companyId: number,
    productId: number,
    request: UpdateProductRequest,
    thumbnailFile?: File | null,
    detailPageFiles?: File[] | null,
  ): Promise<ProductDto> {
    try {
      return await this.companyProductRepository.updateProduct(
        companyId,
        productId,
        request,
        thumbnailFile,
        detailPageFiles,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "상품을 업데이트하지 못했습니다.",
        "PRODUCT_UPDATE_FAILED",
        error,
      );
    }
  }

  async deleteProduct(companyId: number, productId: number): Promise<boolean> {
    try {
      return await this.companyProductRepository.deleteProduct(
        companyId,
        productId,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "상품을 삭제하지 못했습니다.",
        "PRODUCT_DELETE_FAILED",
        error,
      );
    }
  }

  async linkProductOptions(
    companyId: number,
    productId: number,
    request: LinkProductOptionsRequest,
  ): Promise<ProductDto> {
    try {
      return await this.companyProductRepository.linkProductOptions(
        companyId,
        productId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "상품 옵션을 링크하지 못했습니다.",
        "PRODUCT_OPTIONS_LINK_FAILED",
        error,
      );
    }
  }

  async unlinkProductOption(
    companyId: number,
    productId: number,
    productOptionId: number,
  ): Promise<ProductDto> {
    try {
      return await this.companyProductRepository.unlinkProductOption(
        companyId,
        productId,
        productOptionId,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "상품 옵션을 링크 해제하지 못했습니다.",
        "PRODUCT_OPTIONS_UNLINK_FAILED",
        error,
      );
    }
  }

  async getDetailPages(
    companyId: number,
    productId: number,
  ): Promise<DetailPageDto[]> {
    try {
      return await this.companyProductRepository.getDetailPages(
        companyId,
        productId
      );
    } catch (error) {
      throw new AppError(
        String(error) || "상품 옵션을 링크 해제하지 못했습니다.",
        "PRODUCT_OPTIONS_UNLINK_FAILED",
        error,
      );
    }
  }

  async addDetailPages(
    companyId: number,
    productId: number,
    files: File[],
  ): Promise<DetailPageDto[]> {
    try {
      return await this.companyProductRepository.addDetailPages(
        companyId,
        productId,
        files,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "옵션을 불러오지 못했습니다.",
        "PRODUCT_GET_FAILED",
        error,
      );
    }
  }

  async insertDetailPageAt(
    companyId: number,
    productId: number,
    file: File,
    afterDetailPageId: number,
  ): Promise<DetailPageDto[]> {
    try {
      return await this.companyProductRepository.insertDetailPageAt(
        companyId,
        productId,
        file,
        afterDetailPageId
      );
    } catch (error) {
      throw new AppError(
        String(error) || "옵션을 불러오지 못했습니다.",
        "PRODUCT_GET_FAILED",
        error,
      );
    }
  }


  async replaceDetailPages(
    companyId: number,
    productId: number,
    files: File[],
  ): Promise<DetailPageDto[]> {
    try {
      return await this.companyProductRepository.replaceDetailPages(
        companyId,
        productId,
        files,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "옵션을 불러오지 못했습니다.",
        "PRODUCT_GET_FAILED",
        error,
      );
    }
  }

  async updateDetailPageOrders(
    companyId: number,
    productId: number,
    detailPageIds: number[],
  ): Promise<DetailPageDto[]> {
    try {
      return await this.companyProductRepository.updateDetailPageOrders(
        companyId,
        productId,
        detailPageIds,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "옵션을 불러오지 못했습니다.",
        "PRODUCT_GET_FAILED",
        error,
      );
    }
  }

  async deleteDetailPage(
    companyId: number,
    productId: number,
    detailPageId: number,
  ): Promise<boolean> {
    try {
      return await this.companyProductRepository.deleteDetailPage(
        companyId,
        productId,
        detailPageId,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "옵션을 불러오지 못했습니다.",
        "PRODUCT_GET_FAILED",
        error,
      );
    }
  }

  async deleteAllDetailPages(
    companyId: number,
    productId: number,
  ): Promise<boolean> {
    try {
      return await this.companyProductRepository.deleteAllDetailPages(
        companyId,
        productId,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "옵션을 불러오지 못했습니다.",
        "PRODUCT_GET_FAILED",
        error,
      );
    }
  }
}
